import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCcw, ChevronLeft } from "lucide-react";
import { SectionHead } from "@/components/Reveal";
import { ASSESSMENT_QUESTIONS, ASSESSMENT_TIERS } from "@/data/content";

const Assessment = () => {
  const [answers, setAnswers] = useState([]);
  const done = answers.length === ASSESSMENT_QUESTIONS.length;

  const pick = (score) => {
    const next = [...answers, score];
    setTimeout(() => setAnswers(next), 280);
  };

  const back = () => {
    if (answers.length > 0) setAnswers(answers.slice(0, -1));
  };

  const restart = () => {
    setAnswers([]);
  };

  const total = answers.reduce((a, b) => a + b, 0);
  const maxScore = ASSESSMENT_QUESTIONS.length * 3;
  const tier = ASSESSMENT_TIERS.find((t) => total <= t.max);
  const current = ASSESSMENT_QUESTIONS[Math.min(answers.length, ASSESSMENT_QUESTIONS.length - 1)];

  return (
    <section
      id="assessment"
      data-testid="assessment-tool"
      className="relative border-t border-[#27272A] py-24 sm:py-32"
    >
      <div
        className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[720px] -translate-x-1/2 blur-[160px]"
        style={{ background: "radial-gradient(ellipse, rgba(204,10,20,0.1), transparent 65%)" }}
      />
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <SectionHead
          eyebrow="Self-Assessment — 60 Seconds"
          title="Where do you actually stand? Answer four questions. Get your protocol."
          testId="assessment-heading"
        />

        <div className="mt-12 border border-[#27272A] bg-[#121214] p-7 sm:p-10">
          <AnimatePresence mode="wait">
            {!done ? (
              <motion.div
                key={`q-${answers.length}`}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="mb-8 flex items-center justify-between">
                  <span className="font-jb text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                    Question {String(answers.length + 1).padStart(2, "0")} /{" "}
                    {String(ASSESSMENT_QUESTIONS.length).padStart(2, "0")}
                  </span>
                  <div className="flex gap-1.5">
                    {ASSESSMENT_QUESTIONS.map((_, i) => (
                      <span
                        key={i}
                        className={`h-1 w-8 transition-colors duration-300 ${
                          i < answers.length ? "bg-[#CC0A14]" : "bg-[#27272A]"
                        }`}
                      />
                    ))}
                  </div>
                </div>

                <h3
                  data-testid="assessment-question"
                  className="font-syne text-xl font-bold tracking-tight text-white sm:text-2xl"
                >
                  {current.q}
                </h3>

                <div className="mt-8 space-y-3">
                  {current.options.map((opt, i) => (
                    <button
                      key={opt.label}
                      data-testid={`assessment-option-${i}`}
                      onClick={() => pick(opt.score)}
                      className="group flex w-full items-center gap-4 border border-[#27272A] bg-[#0c0c0e] px-5 py-4 text-left transition-colors duration-300 hover:border-[#CC0A14] hover:bg-[#1f181b]"
                    >
                      <span className="font-jb text-[11px] uppercase tracking-[0.2em] text-zinc-600 transition-colors duration-300 group-hover:text-[#CC0A14]">
                        {String.fromCharCode(65 + i)}
                      </span>
                      <span className="text-sm text-zinc-300 transition-colors duration-300 group-hover:text-white">
                        {opt.label}
                      </span>
                    </button>
                  ))}
                </div>

                {answers.length > 0 && (
                  <button
                    data-testid="assessment-back-button"
                    onClick={back}
                    className="mt-6 flex items-center gap-2 font-jb text-[11px] uppercase tracking-[0.2em] text-zinc-500 transition-colors duration-300 hover:text-white"
                  >
                    <ChevronLeft size={14} /> Back
                  </button>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="result"
                data-testid="assessment-result"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-4">
                  <span className="font-jb text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                    Your reading
                  </span>
                  <span className="font-jb text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                    Score {total}/{maxScore}
                  </span>
                </div>
                <p className="mt-5 font-syne text-3xl font-extrabold uppercase tracking-tight text-[#CC0A14] sm:text-4xl">
                  {tier.name}
                </p>
                <p className="mt-5 text-sm leading-relaxed text-zinc-300 sm:text-base">
                  {tier.verdict}
                </p>
                <div className="mt-8 border-t border-[#27272A] pt-7">
                  <p className="font-jb text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                    Prescribed protocol
                  </p>
                  <ol className="mt-5 space-y-4">
                    {tier.protocol.map((p, i) => (
                      <li key={p} className="flex items-start gap-4">
                        <span className="font-syne text-lg font-extrabold text-[#CC0A14]">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="text-sm leading-relaxed text-zinc-300">{p}</span>
                      </li>
                    ))}
                  </ol>
                </div>
                <button
                  data-testid="assessment-restart-button"
                  onClick={restart}
                  className="mt-9 flex items-center gap-3 border border-[#27272A] px-6 py-3.5 font-jb text-xs font-semibold uppercase tracking-[0.22em] text-zinc-300 transition-colors duration-300 hover:border-[#CC0A14] hover:text-white"
                >
                  <RotateCcw size={14} /> Retake
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Assessment;
